
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SubscriptionService from "services/subscription-service";
import DataTable from "examples/Tables/DataTable";
import WorkspaceService from "services/workspace-service";
import BillingService from "services/billing-service";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PlanDetails from "layouts/profile/components/Subscription/planDetails";
import { formatDateToDisplay } from "utils/dateUtils";

// ⭐ NEW HOOK IMPORT
import useWorkspaceSubscription from "hooks/useWorkspaceSubscription";

import Tooltip from "@mui/material/Tooltip";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import UpgradePopup from "layouts/components/Upgrade"; // [NEW]
import { usePermissions } from "context"; // [FIXED]
import LogoutIcon from "@mui/icons-material/Logout"; // [NEW]
import MoreVertIcon from "@mui/icons-material/MoreVert"; // [NEW]
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"; // [NEW]
import Menu from "@mui/material/Menu"; // [NEW]
import MenuItem from "@mui/material/MenuItem"; // [NEW]
import ListItemIcon from "@mui/material/ListItemIcon"; // [NEW]
import CircularProgress from "@mui/material/CircularProgress"; // [NEW]
import UserService from "services/user-service"; // [NEW]
import { AuthContext } from "context"; // [NEW]
import AuthService from "services/auth-service"; // [NEW] Required for logout API call

const Plan = ({ onClose, canClose = true }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const authContext = React.useContext(AuthContext); // [NEW]

  // ⭐ Replace props with hook data
  const { activeSubscription, pendingSubscription, refresh } = useWorkspaceSubscription();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("Annual");

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedPlan, setSelectedPlanState] = useState(null);

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [planDiscounts, setPlanDiscounts] = useState({});

  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
  const [showRBACPopup, setShowRBACPopup] = useState(false); // [NEW] RBAC Popup State

  const { hasPermission } = usePermissions(); // [NEW]
  const canManageBilling = hasPermission("billing:manage"); // [NEW]

  // [NEW] Kebab menu state
  const [kebabAnchor, setKebabAnchor] = useState(null);
  const handleKebabOpen = (e) => setKebabAnchor(e.currentTarget);
  const handleKebabClose = () => setKebabAnchor(null);

  // [NEW] Workspace switcher state
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  const handleWorkspaceMenuOpen = async (e) => {
    setWorkspaceMenuAnchor(e.currentTarget);
    setLoadingWorkspaces(true);
    try {
      const response = await UserService.fetchUserWorkspaces();
      const list = Array.isArray(response) ? response : (response.data || []);
      setWorkspaces(list);
    } catch (err) {
      console.error("Failed to fetch workspaces", err);
    } finally {
      setLoadingWorkspaces(false);
    }
  };
  const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);

  const handleSwitchWorkspace = async (workspaceId) => {
    try {
      await UserService.switchActiveWorkspace(workspaceId);
      localStorage.setItem("activeWorkspaceId", workspaceId);
      window.dispatchEvent(new CustomEvent("profile-updated"));
      handleWorkspaceMenuClose();
      handleKebabClose();
    } catch (err) {
      console.error("Failed to switch workspace", err);
    }
  };

  console.log("Active Subscription inside plan : ", activeSubscription);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await SubscriptionService.fetchSubscription();
        const HIDDEN_PLANS = ["Trial", "Demo", "Labs", "Live Demo"];
        const filteredPlans = response.filter((plan) => !HIDDEN_PLANS.includes(plan.name));
        const sortedPlans = filteredPlans.sort((a, b) => a.code - b.code);

        setPlans(sortedPlans);

        const discountMap = {};
        filteredPlans.forEach(plan => {
          discountMap[plan.name.toLowerCase()] = plan.annual_discount
            ? plan.annual_discount / 100
            : 0;
        });

        setPlanDiscounts(discountMap);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleLogOut = async () => {
    try {
      await AuthService.logout();
      authContext.logout();
      navigate("/auth/login");
      window.location.reload(); // Hard refresh to clear cached states fully
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handleSelectPlan = (plan) => {
    // [NEW] RBAC Restriction
    if (!canManageBilling) {
      setShowRBACPopup(true);
      return;
    }

    // [NEW] Live Demo Restriction
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setShowDemoPopup(true);
      return;
    }

    setSelectedPlanState(plan);
    setOpenConfirmDialog(true);
  };

  const confirmPlanChange = async () => {
    setOpenConfirmDialog(false);

    if (!selectedPlan) return;

    const currentSubscription = activeSubscription || {};
    const isNewPurchase = !activeSubscription;

    const isSamePlan = selectedPlan.code === currentSubscription.code;
    const isSameCycle = billingCycle === currentSubscription.billingCycle;

    const isUpgrade =
      isNewPurchase ||
      selectedPlan.code > currentSubscription.code ||
      (isSamePlan &&
        currentSubscription.billingCycle === "Monthly" &&
        billingCycle === "Annual");

    const isDowngrade =
      !isNewPurchase &&
      (selectedPlan.code < currentSubscription.code ||
        (isSamePlan &&
          currentSubscription.billingCycle === "Annual" &&
          billingCycle === "Monthly"));

    if (isSamePlan && isSameCycle) {
      setMessage("You're already on this plan.");
      setMessageType("info");
      return;
    }

    // --- Upgrade logic (unchanged)
    if (isUpgrade) {
      try {
        const priceId =
          billingCycle === "Monthly"
            ? selectedPlan.stripe_price_id_monthly
            : selectedPlan.stripe_price_id_annual;

        const currentWorkspaceId = localStorage.getItem("activeWorkspaceId");
        const checkoutUrl = await BillingService.createCheckoutSession(priceId, currentWorkspaceId);
        window.location.href = checkoutUrl;
      } catch (err) {
        console.error("Stripe checkout failed:", err.message);
        setMessage("Payment failed. Please try again.");
        setMessageType("error");
      }
      return;
    }

    // --- Downgrade logic (unchanged)
    if (isDowngrade) {
      try {
        const [day, month, year] = activeSubscription.renewalDate
          .split("/")
          .map(Number);

        const startDate = new Date(year, month - 1, day);
        startDate.setDate(startDate.getDate() + 1);

        const nextRenewalDate = new Date(startDate);
        if (billingCycle === "Annual") {
          nextRenewalDate.setFullYear(nextRenewalDate.getFullYear() + 1);
        } else {
          nextRenewalDate.setMonth(nextRenewalDate.getMonth() + 1);
        }

        const subscriptionInfo = {
          name: selectedPlan.name,
          billing_cycle: billingCycle,
          auto_renew: activeSubscription.autoRenewal,
          start_date: startDate,
          next_renewal_date: nextRenewalDate,
          stripe_subscription_id: activeSubscription.stripe_subscription_id,
          stripe_customer_id: activeSubscription.stripe_customer_id,
        };

        await WorkspaceService.saveWorkspaceSubscription(subscriptionInfo);

        const newPriceId =
          billingCycle === "Monthly"
            ? selectedPlan.stripe_price_id_monthly
            : selectedPlan.stripe_price_id_annual;

        await BillingService.scheduleDowngrade(newPriceId);

        setMessage(
          `Your downgrade to ${selectedPlan.name} (${billingCycle}) is scheduled for ${formatDateToDisplay(
            activeSubscription.renewalDate
          )}.`
        );

        setMessageType("success");

        // ⭐ Refresh active & pending subscription states
        refresh();

      } catch (err) {
        console.error("Failed to schedule downgrade:", err.message);
        setMessage("Failed to schedule downgrade. Please try again.");
        setMessageType("error");
      }

      return;
    }

    setMessage("Plan change failed. Please contact support.");
    setMessageType("error");
  };

  const colorsMap = {
    "starter plan": "#FCE4EC",
    "basic plan": "#E3F2FD",
    "pro plan": "#FFF3E0",
    "ultra plan": "#F1F8E9",
  };

  // if (loading) {
  //   return (
  //     <MDTypography variant="body2" color="textSecondary">
  //       Loading plans...
  //     </MDTypography>
  //   );
  // }

  // if (!activeSubscription && !message) {
  //   setMessage("No Active subscription found. Choose a subscription to use Comparedly");
  //   setMessageType("error");
  // }

  // --- Table generation remains untouched ---
  const allFeatures = [...new Set(plans.flatMap((p) => p.features))];

  const columns = [
    {
      Header: (
        <MDBox
          textAlign="center"
          sx={{
            mt: 10,
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 2,
          }}
        >
          <MDTypography variant="h6" fontWeight="bold">
            Features
          </MDTypography>
        </MDBox>
      ),
      accessor: "feature",
      align: "left",
    },
    ...plans.map((plan) => {
      const discountRate = planDiscounts[plan.name.toLowerCase()] || 0;

      const price =
        billingCycle === "Monthly"
          ? plan.price / 100
          : (plan.price * (1 - discountRate)) / 100;

      return {
        Header: (
          <PlanDetails
            plan={plan}
            billingCycle={billingCycle}
            discountRate={discountRate}
            price={price}
            isProPlan={plan.name.toLowerCase() === "pro plan"}
            colorsMap={colorsMap}
          />
        ),
        accessor: plan.name,
        align: "center",
      };
    }),
  ];

  const rows = [];

  rows.push({
    feature: "",
    ...plans.reduce((acc, plan) => {
      acc[plan.name] = (
        <MDButton
          variant="contained"
          color="accent"
          size="small"
          onClick={() => handleSelectPlan(plan)}
        >
          Select
        </MDButton>
      );
      return acc;
    }, {}),
  });

  // [NEW] Feature Descriptions
  const featureDescriptions = {
    // Static Metrics
    "Social Profiles": "The total number of profiles you can track across all platforms.",
    "Profile Swaps per month": "How many times you can remove and add a new profile within a billing cycle.",
    "Snapshots": "The number of automated, recurring snapshots you can schedule simultaneously.",
    "Reports": "The total number of reports you can create and manage in your library.",

    // Features
    "Audience Report": "Deep dive into audience demographics, growth trends, and locations.",
    "Report": "Generate detailed, customisable social media reports.",
    "Demographics Report": "Analyse audience age, gender, and location distribution.", // ✅ NEW
    "Engagement Report": "Metrics on how users interact with content (likes, comments, shares).",
    "Content Drill Down": "Detailed performance analysis of individual posts and videos.",
    "Competitor Analysis": "Benchmark performance against direct competitors.",
    "Discover & Organise": "Tools to find new competitors and organize them into groups.",
    "Report Download": "Export data and charts as PDF or CSV files.",
    "Report Sharing": "Share interactive report links with team members or clients.",
    "Sentiment Timeline": "Track how audience sentiment (positive/negative) changes over time.",
    "Word Cloud": "Visual representation of the most frequently used words in comments.",
    "Reach Chart": "Analysis of unique users who saw the content.",
    "Views Chart": "Total number of times content was viewed.",
    "Comment Sentiment": "AI-powered analysis of comment tone and emotion.",
    "Snapshot": "Create point-in-time reports for specific profiles.",
    "Snapshot Scheduling": "Automate report generation on a monthly basis.",
    "Report Scheduling": "Schedule and automate report generation and delivery.",
    "Optimal Content Frequency": "Insights on how often to post for maximum engagement.",
    "Optimal Posting Time": "Best times of day and days of week to publish content.",
    "Optimal Content Format": "Analysis of which content types (video, image, text) perform best.",
    "AI Powered Insight": "Get instant analysis and answers about your data directly from your charts using AI.",
    "Extended History Data": "Access to historical data beyond the standard retention period."
  };

  // [NEW] Add static metrics rows (Profiles, Swaps, Snapshots)
  const staticMetrics = [
    { label: "Social Profiles", key: "profiles" },
    { label: "Profile Swaps per month", key: "profile_swaps" },
    { label: "Snapshots", key: "snapshots" },
    { label: "Reports", key: "reports" },
  ];

  staticMetrics.forEach((metric) => {
    const row = {
      feature: (
        <MDBox display="flex" alignItems="center">
          <MDTypography variant="button" color="text" fontWeight="bold">
            {metric.label}
          </MDTypography>
          {featureDescriptions[metric.label] && (
            <Tooltip title={featureDescriptions[metric.label]} placement="right" arrow>
              <MDBox display="flex" alignItems="center" ml={0.5} sx={{ cursor: "pointer", color: "text.secondary" }}>
                <InfoOutlinedIcon fontSize="small" sx={{ width: "16px", height: "16px" }} />
              </MDBox>
            </Tooltip>
          )}
        </MDBox>
      )
    };
    plans.forEach((plan) => {
      // Check if value exists, otherwise show "-"
      // Also maybe format numbers if needed, but raw number is fine for now
      const value = plan[metric.key];
      row[plan.name] = (
        <MDTypography variant="button" fontWeight="bold" color="text">
          {value !== undefined && value !== null ? value : "-"}
        </MDTypography>
      );
    });
    rows.push(row);
  });

  allFeatures.forEach((feature) => {
    const row = {
      feature: (
        <MDBox display="flex" alignItems="center">
          <MDTypography variant="button" color="text" fontWeight="medium">
            {feature}
          </MDTypography>
          {featureDescriptions[feature] && (
            <Tooltip title={featureDescriptions[feature]} placement="right" arrow>
              <MDBox display="flex" alignItems="center" ml={0.5} sx={{ cursor: "pointer", color: "text.secondary" }}>
                <InfoOutlinedIcon fontSize="small" sx={{ width: "16px", height: "16px" }} />
              </MDBox>
            </Tooltip>
          )}
        </MDBox>
      )
    };
    plans.forEach((plan) => {
      row[plan.name] = plan.features.includes(feature) ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      );
    });
    rows.push(row);
  });

  return (
    <Card>
      {/* ---------------------- TOP BAR ---------------------- */}
      <MDBox p={2} display="flex" justifyContent="center" alignItems="center" position="relative">

        {/* Centred title */}
        <MDTypography variant="h3" fontWeight="bold" textAlign="center">
          Compare Subscription Plans
        </MDTypography>

        {/* Right controls: 3-dot + optional close */}
        <MDBox sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* 3-dot kebab */}
          <IconButton size="small" onClick={handleKebabOpen} sx={{ color: "text.secondary" }}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={kebabAnchor}
            open={Boolean(kebabAnchor)}
            onClose={handleKebabClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 0.5, zIndex: 9999 }}
          >
            {/* Switch Workspace */}
            <MenuItem onClick={handleWorkspaceMenuOpen}>
              <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
              Switch Workspace
            </MenuItem>
            {/* Nested workspace list */}
            <Menu
              anchorEl={workspaceMenuAnchor}
              open={Boolean(workspaceMenuAnchor)}
              onClose={handleWorkspaceMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ zIndex: 10000 }}
            >
              {loadingWorkspaces ? (
                <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading...</MenuItem>
              ) : workspaces.length > 0 ? (
                workspaces.map((ws) => (
                  <MenuItem
                    key={ws.workspace_id?._id || ws._id}
                    onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
                    selected={ws.is_active}
                  >
                    {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No other workspaces</MenuItem>
              )}
            </Menu>
            {/* Log Out */}
            <MenuItem onClick={() => { handleKebabClose(); handleLogOut(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Log Out
            </MenuItem>
          </Menu>

          {/* Close button (only in dialog mode with canClose) */}
          {onClose && canClose && (
            <IconButton onClick={onClose} sx={{ color: "gray" }}>
              <CloseIcon />
            </IconButton>
          )}
        </MDBox>
      </MDBox>

      {/* ---------------------- LOADING STATE ---------------------- */}
      {loading ? (
        <MDTypography
          variant="body2"
          color="text"
          textAlign="center"
          sx={{ py: 3 }}
        >
          Loading plans...
        </MDTypography>
      ) : (
        <>
          {/* ---------------------- CURRENT PLAN INFO ---------------------- */}
          {activeSubscription && (
            <MDBox textAlign="center" my={2}>
              <MDTypography variant="h6" color="secondary">
                Current Plan: {activeSubscription.plan} ({activeSubscription.billingCycle} Renewal)
              </MDTypography>
            </MDBox>
          )}

          {/* ---------------------- MESSAGES ---------------------- */}
          {message && (
            <MDBox textAlign="center" mt={1}>
              <MDTypography
                variant="body2"
                color={messageType === "success" ? "success.main" : "error.main"}
                sx={{ whiteSpace: "pre-line" }}
              >
                {message}
              </MDTypography>
            </MDBox>
          )}

          {/* ---------------------- BILLING CYCLE TOGGLE ---------------------- */}
          <MDBox display="flex" justifyContent="center" my={2}>
            <ButtonGroup variant="outlined" sx={{ borderColor: "#00A1A1" }}>
              <Button
                onClick={() => setBillingCycle("Monthly")}
                sx={{
                  backgroundColor:
                    billingCycle === "Monthly" ? "#00A1A1" : "transparent",
                  color: billingCycle === "Monthly" ? "#fff" : "#00A1A1",
                  fontWeight: "bold",
                  borderColor: "#00A1A1",
                  "&:hover": {
                    backgroundColor:
                      billingCycle === "Monthly" ? "#007777" : "transparent",
                    color:
                      billingCycle === "Monthly" ? "#fff" : "#007777",
                    borderColor: "#007777",
                  },
                }}
              >
                Monthly Billing
              </Button>

              <Button
                onClick={() => setBillingCycle("Annual")}
                sx={{
                  backgroundColor:
                    billingCycle === "Annual" ? "#00A1A1" : "transparent",
                  color: billingCycle === "Annual" ? "#fff" : "#00A1A1",
                  fontWeight: "bold",
                  borderColor: "#00A1A1",
                  "&:hover": {
                    backgroundColor:
                      billingCycle === "Annual" ? "#007777" : "transparent",
                    color:
                      billingCycle === "Annual" ? "#fff" : "#007777",
                    borderColor: "#007777",
                  },
                }}
              >
                Annual Billing
              </Button>
            </ButtonGroup>
          </MDBox>

          {/* ---------------------- DATA TABLE ---------------------- */}
          <MDBox>
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder={true}
              entriesPerPage={false}
              tablePageSize={500} // Show all features in one view (remove pagination)
            />
          </MDBox>
        </>
      )}

      {/* ---------------------- CONFIRMATION DIALOG ---------------------- */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle>Confirm Plan Change</DialogTitle>
        <DialogContent>
          <MDTypography variant="subtitle2">
            {pendingSubscription?.code &&
              selectedPlan?.code === pendingSubscription?.code &&
              billingCycle === pendingSubscription?.billingCycle ? (
              <>
                You have already scheduled to move to{" "}
                <strong>
                  {selectedPlan?.name} ({billingCycle})
                </strong>
                .
              </>
            ) : selectedPlan?.code === activeSubscription?.code &&
              billingCycle === activeSubscription?.billingCycle &&
              !pendingSubscription?.code ? (
              <>
                You are already subscribed to{" "}
                <strong>
                  {selectedPlan?.name} ({billingCycle})
                </strong>
                . No changes needed.
              </>
            ) : (
              <>
                Are you sure you want to change your plan to{" "}
                <strong>
                  {selectedPlan?.name} ({billingCycle})
                </strong>
                ?
              </>
            )}
          </MDTypography>
        </DialogContent>

        <DialogActions>
          {pendingSubscription?.code &&
            selectedPlan?.code === pendingSubscription?.code &&
            billingCycle === pendingSubscription?.billingCycle ? (
            <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
              Ok
            </Button>
          ) : selectedPlan?.code === activeSubscription?.code &&
            billingCycle === activeSubscription?.billingCycle &&
            !pendingSubscription?.code ? (
            <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
              Ok
            </Button>
          ) : (
            <>
              <Button onClick={confirmPlanChange} color="primary">
                Confirm
              </Button>
              <Button onClick={() => setOpenConfirmDialog(false)} color="error">
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName="Changing Subscription Plan"
      />

      {/* [NEW] RBAC Restriction Popup */}
      <UpgradePopup
        type="rbac"
        open={showRBACPopup}
        onClose={() => setShowRBACPopup(false)}
        roleRequired="Owner"
        message="Only Workspace Owners can modify subscription and billing settings."
      />
    </Card >
  );

};

export default Plan;
